import {
  AutomationTiming,
  MarketingCampaign,
  RecurrenceDirection,
  TimezoneResolver,
} from '@principle-theorem/principle-core';
import {
  IBrand,
  IMarketingCampaign,
  IMarketingCampaignStep,
  MarketingCampaignStepStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Timezone,
  WithRef,
  mergeDayAndTime,
  multiFilter,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export interface IMarketingCampaignStepSummary {
  activeSteps: number;
  durationInDays: number;
}

export class MarketingCampaignStepSummary {
  static async getSummary(
    brandRef: DocumentReference<IBrand>,
    activeSteps: IMarketingCampaignStep[]
  ): Promise<IMarketingCampaignStepSummary> {
    const timezone = await TimezoneResolver.fromBrandRef(brandRef);
    return {
      activeSteps: activeSteps.length,
      durationInDays: this.getDurationInDays(activeSteps, timezone),
    };
  }

  static getStepSummary$(
    campaign: WithRef<IMarketingCampaign>
  ): Observable<IMarketingCampaignStepSummary> {
    return MarketingCampaignStepSummary.getActiveSteps$(campaign).pipe(
      switchMap((steps) =>
        MarketingCampaignStepSummary.getSummary(campaign.brandRef, steps)
      )
    );
  }

  static getActiveSteps$(
    campaign: WithRef<IMarketingCampaign>
  ): Observable<WithRef<IMarketingCampaignStep>[]> {
    return MarketingCampaign.orderedSteps$(campaign).pipe(
      multiFilter((step) => step.status === MarketingCampaignStepStatus.Active)
    );
  }

  static getDurationInDays(
    steps: IMarketingCampaignStep[],
    timezone: Timezone
  ): number {
    if (steps.length === 0) {
      return 0;
    }
    const start = moment.tz(timezone);

    const end = steps.reduce((last: moment.Moment, step) => {
      const nextValidOccurrence = AutomationTiming.getNextValidOccurrence(
        last.clone(),
        step.config.timing.daysOfWeek,
        RecurrenceDirection.Forward,
        timezone,
        step.config.timing.countOnlySelectedDays ?? false,
        step.config.timing.unit,
        step.config.timing.amount
      );
      if (!nextValidOccurrence) {
        return last.clone();
      }
      if (!step.config.timing.time) {
        return nextValidOccurrence;
      }
      return mergeDayAndTime(
        nextValidOccurrence,
        step.config.timing.time,
        timezone
      );
    }, start.clone());

    const startOfFirstDay = start.clone().startOf('day');
    const startOfLastDay = end.clone().startOf('day');
    const days = startOfLastDay.diff(startOfFirstDay, 'days');
    return days > 1 ? days : 1;
  }
}
